/**
 * ListExportPreview
 *
 * A reusable print preview page for exporting list data as PDF.
 * Uses the standard UCI CMMS header/footer structure.
 *
 * Usage: Navigate to this page with state containing:
 * - title: Document title (e.g., "Assets List")
 * - data: Array of data to display (pre-processed with __export_ prefixed values)
 * - columns: Column definitions with field, headerName
 * - filters: Optional object describing active filters
 * - subtitle: Optional subtitle text
 */
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material'
import { Print, ArrowBack } from '@mui/icons-material'
import { Button, DocumentQRCode, PrintStyles, DocumentBrandHeader, PoweredByDuoNep } from '@components/common'
import { useDocumentRegistry } from '@hooks/useDocumentRegistry'
import { useAuth } from '@context/AuthContext'
import { formatDateTime } from '@utils/dateUtils'

const ListExportPreview = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { registerDocument, verificationCode, loading: registering } = useDocumentRegistry()

  const {
    title = 'Data Export',
    data = [],
    columns = [],
    filters = {},
    subtitle = '',
    backPath = -1,
  } = location.state || {}

  // Set document title for PDF filename
  useEffect(() => {
    const originalTitle = document.title
    const date = new Date().toISOString().split('T')[0]
    document.title = `${title} - ${date} - DuoNep`

    return () => {
      document.title = originalTitle
    }
  }, [title])

  // Generate a unique document reference for list exports
  const generateDocumentNumber = () => {
    const now = new Date()
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '')
    const timeStr = now.toTimeString().slice(0, 5).replace(':', '')
    return `${title} - ${dateStr}-${timeStr} (${data.length} records)`
  }

  // Register document when data is loaded
  useEffect(() => {
    if (data.length > 0 && !verificationCode) {
      const documentNumber = generateDocumentNumber()
      const documentData = {
        title,
        document_number: documentNumber,
        columns: columns.map(c => c.headerName),
        filters,
        record_count: data.length,
        data_hash: JSON.stringify(data.map(d => d.id || d)).substring(0, 1000),
      }
      registerDocument('list_export', documentNumber, null, documentData, {
        columns: columns.map(c => ({ field: c.field, header: c.headerName })),
        filters,
        record_count: data.length,
        exported_at: new Date().toISOString(),
      }).catch((err) => console.error('Failed to register document:', err))
    }
  }, [data, title, columns, filters, verificationCode, registerDocument])

  const handlePrint = async () => {
    // Ensure document is registered before printing
    if (!verificationCode && data.length > 0) {
      try {
        const documentNumber = generateDocumentNumber()
        const documentData = {
          title,
          document_number: documentNumber,
          columns: columns.map(c => c.headerName),
          filters,
          record_count: data.length,
          data_hash: JSON.stringify(data.map(d => d.id || d)).substring(0, 1000),
        }
        await registerDocument('list_export', documentNumber, null, documentData, {
          columns: columns.map(c => ({ field: c.field, header: c.headerName })),
          filters,
          record_count: data.length,
          exported_at: new Date().toISOString(),
        })
      } catch (err) {
        console.error('Registration failed:', err)
      }
    }
    window.print()
  }

  // Filter out action columns
  const exportColumns = columns.filter(col => col.field !== 'actions' && !col.noExport)

  // Format active filters for display
  const activeFilters = Object.entries(filters).filter(([_, value]) => value)

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No data to export
        </Typography>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate(backPath)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    )
  }

  // Get display value for a cell - check for pre-processed __export_ value first
  const getCellValue = (row, column) => {
    const exportKey = `__export_${column.field}`
    if (row[exportKey] !== undefined) {
      return row[exportKey]
    }
    return row[column.field]
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <Box className="print-controls no-print" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate(backPath)}
        >
          Back
        </Button>
        <Button
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={registering}
        >
          {registering ? 'Preparing...' : 'Print / Save as PDF'}
        </Button>
      </Box>

      {/* Printable Content */}
      <Box className="print-content" sx={{ bgcolor: 'white' }}>
        {/* Co-branded document header */}
        <DocumentBrandHeader title={title.toUpperCase()} subtitle={subtitle} />

        {/* Active Filters Display */}
        {activeFilters.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" fontWeight="bold">Filters:</Typography>
            {activeFilters.map(([key, value]) => (
              <Chip
                key={key}
                label={`${key}: ${value}`}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        )}

        {/* Summary */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            <strong>Total Records:</strong> {data.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generated: {formatDateTime(new Date())}
          </Typography>
        </Box>

        {/* Data Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold', width: 50 }}>#</TableCell>
                {exportColumns.map((column) => (
                  <TableCell
                    key={column.field}
                    sx={{ fontWeight: 'bold' }}
                    align={column.align || 'left'}
                  >
                    {column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={row.id || index}>
                  <TableCell>{index + 1}</TableCell>
                  {exportColumns.map((column) => {
                    const value = getCellValue(row, column)
                    return (
                      <TableCell key={column.field} align={column.align || 'left'}>
                        {value ?? '-'}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Printed: {formatDateTime(new Date())}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Printed by: {(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : user?.username || 'N/A'}
            </Typography>
          </Box>
          <DocumentQRCode
            verificationCode={verificationCode}
            documentType="List Export"
            documentNumber={title}
            size={80}
          />
        </Box>

        {/* Powered by DuoNep */}
        <PoweredByDuoNep />
      </Box>

      <PrintStyles orientation="landscape" />
    </>
  )
}

export default ListExportPreview
